import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React, { useContext } from "react";
import { MdHighlightOff, MdKeyboardArrowDown, MdKeyboardArrowRight, MdHistory,MdDownload } from "react-icons/md";
import { FaSpinner } from "react-icons/fa";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import { scheduleSettlementLogs } from "api/settlement";
import { toast } from "react-hot-toast";
import {downloadSettlementReport} from "api/settlement";
import ExcelJS from "exceljs";
import {saveAs} from "file-saver";
import summaryReportExcelSheets from "./settlementReportTypes/summary/summaryReports";
import apmReportsExcelSheets from "./settlementReportTypes/apm/apmTransaction";
import cardTypeVisaReportsExcelSheets from "./settlementReportTypes/cards/CardTypeVisaReports";
import cardTypeDiscoverReportsExcelSheets from "./settlementReportTypes/cards/CardTypeDiscoverReports";
import cardTypeAmexReportsExcelSheets from "./settlementReportTypes/cards/CardTypeAmexReports";
import cardTypeMasterCardReportsExcelSheets from "./settlementReportTypes/cards/CardTypeMasterCardReports";
import refundReportExcelSheets from "./settlementReportTypes/refunded/refundReports";
import cancelReportExcelSheets from "./settlementReportTypes/canceled/cancelReports";
import declinedReportExcelSheets from "./settlementReportTypes/declined/declinedReports";
import disputeReportExcelSheets from "./settlementReportTypes/dispute/disputeReports";
import chargebackReportExcelSheets from "./settlementReportTypes/chargeback/chargebackReports";
import heldReportExcelSheets from "./settlementReportTypes/held/heldReports";
import erroredReportExcelSheets from "./settlementReportTypes/errored/erroredReports";
import { ClientContext } from "ClientProvider";

const SettlementLogs = ({ info }: { info?: any; }) => {
    const { roleData } = useContext(ClientContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [isLogsLoading, setIsLogsLoading] = React.useState(false);
  const [logsData, setLogsData] = React.useState<any>({});
  const [expandedLogId, setExpandedLogId] = React.useState<number | null>(null);
    const [downloadingId, setDownloadingId] = React.useState<number | null>(null);
    
    const toggleLog = (id: number) => {
        setExpandedLogId(prev => prev === id ? null : id);
    };

    const fetchLogs = () => {
    setIsLogsLoading(true);
    scheduleSettlementLogs(info.id)
        .then((data) => {
        if (data.status === "Error") {
            toast.error(data?.message ?? "Something went wrong!");
        } else {
            setLogsData(data)
        }
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? "Settlement logs fetch failed"))
        .finally(() => setIsLogsLoading(false));
    };

    const handleClose = () => {
        setExpandedLogId(null)
        setIsCloseModal(true);
        setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
    };

    const STATUS_STYLES: Record<string, string> = {
        approved: "fp-status-green",
        paid: "fp-status-green",
        unapproved: "fp-status-amber",
        unpaid: "fp-status-rose",
        pending: "fp-status-amber",
        rejected: "fp-status-rose",
    };

    const statusClass = (status?: string) =>
        STATUS_STYLES[(status || "").toLowerCase()] || "fp-status-slate";

    const formatStatus = (status?: string) => {
        if (!status) return "—";
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const createExcel=(jsonData:any,org_name:any,clients:any,payment_methods:any)=>{
        try{
            if(!jsonData?.settlementReportCanBeGenerated){
                toast.error("Cannot generate settlement report.");
                return;
            }

            const workbook=new ExcelJS.Workbook();

            const templateColor={
                yellow:"b59a67",
                blue:"152046",
                black:"070708",
                white:"f5f5fc"
            };

            summaryReportExcelSheets(jsonData,workbook,templateColor);
            apmReportsExcelSheets(jsonData,workbook,templateColor);
            cardTypeAmexReportsExcelSheets(jsonData,workbook,templateColor);
            cardTypeMasterCardReportsExcelSheets(jsonData,workbook,templateColor);
            cardTypeVisaReportsExcelSheets(jsonData,workbook,templateColor);
            cardTypeDiscoverReportsExcelSheets(jsonData,workbook,templateColor);
            refundReportExcelSheets(jsonData,workbook,templateColor);
            cancelReportExcelSheets(jsonData,workbook,templateColor);
            chargebackReportExcelSheets(jsonData,workbook,templateColor);
            disputeReportExcelSheets(jsonData,workbook,templateColor);
            heldReportExcelSheets(jsonData,workbook,templateColor);
            declinedReportExcelSheets(jsonData,workbook,templateColor);
            erroredReportExcelSheets(jsonData,workbook,templateColor);

            const organizationName=org_name??"Organization";
            const clientName=clients?.map((c:any)=>c?.name)?.join(" ")??"Client";
            const paymentMethods=payment_methods??[];

            const startDates=paymentMethods.map((p:any)=>p?.starting_date).filter(Boolean);
            const endDates=paymentMethods.map((p:any)=>p?.ending_date).filter(Boolean);

            const startDate=startDates.length>0?startDates.sort()[0]:"";
            const endDate=endDates.length>0?endDates.sort().reverse()[0]:"";

            const dateRange=startDate&&endDate?`${startDate}_to_${endDate}`:"DateRange";

            const safeFileName=`${organizationName} ${clientName} ${dateRange}`.replace(/[^\w\s-]/g,"");

            workbook.xlsx.writeBuffer().then(buffer=>{
                const blob=new Blob([buffer],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
                saveAs(blob,safeFileName);
            });
        }catch(error){
            toast.error("Failed to generate Excel file.");
        }
    };

    const downloadReport = (settlement_id : any,org_name : any,clients:any,payment_methods:any) => {
        setDownloadingId(settlement_id);
        downloadSettlementReport(settlement_id)
        .then(response=>{
            if(response?.settlementReportCanBeGenerated){
                createExcel(response,org_name,clients,payment_methods);
            }else{
                toast.error("No transactions available for selected period.");
            }
        })
        .catch(()=>{
            toast.error("Download failed.");
        })
        .finally(()=>{
            setDownloadingId(null);
        });
    }

  return (
    <>
    <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
        <PopoverTrigger>
        <button className="fp-info-trigger" onClick={() => {onOpen(); fetchLogs();}} aria-label="View Settlement Logs " onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
            <MdHistory size={14} />
        </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="220px" p="0" overflow="hidden" zIndex="20">
        <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="14px" py="10px">
            View Settlement Logs 
        </PopoverHeader>
        <PopoverArrow bg="#eff6ff" />
        </PopoverContent>
    </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`fp-panel${isOpen && !isCloseModal ? " fp-panel--open" : " fp-panel--close"}`}>
            <div className="fp-panel-glow" />

            <div className="fp-panel-header">
                <div className="fp-panel-brand">
                <div className="fp-panel-logo">
                    <MdHistory size={20} color="#ffffff"/>
                </div>
                <div>
                    <h2 className="fp-panel-title">Settlement Logs</h2>
                    <p className="fp-panel-sub">Detailed of generated settlements logs</p>
                </div>
                </div>
                <button className="fp-panel-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
            </div>

            <div className="fp-panel-body">
                {isLogsLoading ? (
                    <div className="fp-loading">
                        <div className="fp-loading-card" />
                        <div className="fp-loading-card" />
                        <div className="fp-loading-card" />
                    </div>
                ) : (
                    <>
                        <div className="fp-card fp-card-compact">
                            <div className="fp-card-title">
                                <span className="fp-card-title-bar" />
                                Schedule Details
                                <span className={`fp-status-pill fp-status-pill--lg ${info.status === "Active" ? "fp-status-green" : "fp-status-slate"}`}>
                                    {info.status || "Inactive"}
                                </span>
                            </div>

                            <div className="fp-schedule-grid">
                                <div className="fp-schedule-item">
                                    <span className="fp-label">Schedule Type</span>
                                    <span className="fp-value capitalize">{info.schedule_type}</span>
                                </div>
                                <div className="fp-schedule-item">
                                    <span className="fp-label">Execution Time</span>
                                    <span className="fp-value">{info.execution_time}</span>
                                </div>
                                {(info.schedule_type === "weekly" ||
                                    info.schedule_type === "monthly") && (
                                    <div className="fp-schedule-item">
                                        <span className="fp-label">
                                            {info.schedule_type === "weekly" ? "Execution Day"  : "Day of Month"}
                                        </span>
                                        <span className="fp-value">
                                            {info.execution_day || "End of Month"}
                                        </span>
                                    </div>
                                )}
                                <div className="fp-schedule-item">
                                    <span className="fp-label"> Currency </span>
                                    <span className="fp-value"> {info.settlement_details?.settlement_currency} </span>
                                </div>

                                <div className="fp-schedule-item fp-schedule-item-full">
                                    <span className="fp-label"> Scheduled From </span>
                                    <span className="fp-value"> {info.created_at}</span>
                                </div>
                            </div>
                        </div>
                        <div className="fp-history-heading">
                            <div>
                                <div className="fp-section-title"> Generated Settlement History </div>
                                <div className="fp-section-subtitle"> All settlements generated by this schedule </div>
                            </div>
                            <div className="fp-log-count">
                                <span className="fp-log-count-dot" />
                                {logsData?.data?.total_logs || 0} Settlements
                            </div>
                        </div>

                        {logsData?.data?.logs?.length ? (
                            logsData.data.logs.map((item: any) => {
                                const isExpanded = expandedLogId === item.schedule_log.id;
                                return (
                                    <div className={`fp-history-card ${isExpanded ? "fp-history-card--open" : ""}`} key={item.schedule_log.id}>
                                        <div className="fp-history-header" onClick={() => toggleLog(item.schedule_log.id) }>
                                            <div className="fp-history-left">
                                                <div className="fp-history-avatar">
                                                    {item.settlement.organization_name?.charAt(0) || "?"}
                                                </div>
                                                <div className="fp-history-info">
                                                    <div className="fp-org-name">
                                                        {item.settlement.organization_name}
                                                    </div>
                                                    <div className="fp-history-meta">
                                                        <span className="fp-history-date">
                                                            Executed {item.schedule_log.executed_at}
                                                        </span>
                                                    </div>
                                                    <div className="fp-history-meta">
                                                        <span className={`fp-status-pill ${statusClass(item.settlement.settlement_status)}`}>
                                                            {formatStatus(item.settlement.settlement_status)}
                                                        </span>
                                                        <span className={`fp-status-pill ${statusClass(item.settlement.payout_status)}`}>
                                                            {formatStatus(item.settlement.payout_status)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div> 
                                            {/* {roleData?.[0]?.settlement?.value?.download_settlement && ( */}
                                                <div className="fp-history-arrow">
                                                    {downloadingId === item.schedule_log.settlement_id ? (
                                                        <span className="dcm-spinner" />
                                                    ) : (
                                                        <MdDownload size={20} onClick={(e) => { e.stopPropagation(); downloadReport(item.schedule_log.settlement_id,item.settlement.organization_name,item.settlement.clients,item.settlement.payment_methods)}}/>
                                                    )}
                                                </div>
                                            {/* )} */}
                                            <div className="fp-history-arrow">
                                                {isExpanded ? (
                                                    <MdKeyboardArrowDown size={20} />
                                                ) : (
                                                    <MdKeyboardArrowRight size={20} />
                                                )}
                                            </div>
                                        </div>
                                        {isExpanded && (
                                            <div className="fp-history-body">
                                                <div className="fp-history-divider" />
                                                <div className="fp-section-block">
                                                    <div className="fp-history-label">
                                                        <span className="fp-label-icon">👥</span> Clients
                                                    </div>
                                                    <div className="fp-chip-wrap">
                                                        {item.settlement.clients?.map(
                                                            (client: any) => (
                                                                <span key={client.id} className="fp-chip fp-chip-client" > {client.name}</span>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="fp-history-divider" />
                                                <div className="fp-section-block">
                                                    <div className="fp-history-label">
                                                        <span className="fp-label-icon">💳</span> Payment Methods
                                                    </div>
                                                    <div className="fp-payment-list">
                                                        {item.settlement.payment_methods?.map(
                                                            (method: any, index: number) => (
                                                                <div className="fp-payment-item" key={index} >
                                                                    <div className="fp-payment-name"> {method.name} </div>
                                                                    <div className="fp-payment-date">
                                                                        <span> {method.starting_date} </span>
                                                                        <span className="fp-payment-arrow"> → </span>
                                                                        <span>{method.ending_date}</span>
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="fp-empty">
                                <div className="fp-empty-icon"> 📄 </div>
                                <div className="fp-empty-title">  No Generated Settlements  </div>
                                <div className="fp-empty-desc"> This schedule hasn't generated any settlements yet. </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="fp-panel-footer">
                <div className="fp-footer-note">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Read-only settlement scheduled details
                </div>
                <button className="fp-btn-close" onClick={handleClose}>Close</button>
            </div>
        </div>
      </Modal>

        {FP_PANEL_STYLES}
    </>
  );
};

export default SettlementLogs;

/* ── Shared panel styles for all FiatPayout info panels ── */
export const FP_PANEL_STYLES = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    .fp-card{
        background:#fff;
        border:1px solid #e2e8f0;
        border-radius:14px;
        padding:18px;
    }

    .fp-card-title{
        display:flex;
        align-items:center;
        flex-wrap:wrap;
        row-gap:8px;
        gap:9px;
        font-size:14px;
        font-weight:700;
        color:#0f172a;
        margin-bottom:16px;
    }

    .fp-card-title-bar{
        width:4px;
        height:16px;
        border-radius:3px;
        background:linear-gradient(180deg,#2563eb,#7c3aed);
        flex-shrink:0;
    }

    .fp-label{
        font-size:11px;
        font-weight:700;
        color:#94a3b8;
        text-transform:uppercase;
        letter-spacing:.5px;
    }

    .fp-value{
        font-size:14.5px;
        font-weight:700;
        color:#0f172a;
        word-break:break-all;
        font-variant-numeric:tabular-nums;
    }

    .fp-info-trigger {
      width: 28px; height: 28px; border-radius: 7px; background: #f8fafc;
      border: 1.5px solid #e2e8f0; color: #64748b;
      display: flex; align-items: center; justify-content: center;
      transition: all .15s; cursor: pointer;
    }
    .fp-info-trigger:hover { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
    .fp-info-trigger:disabled { opacity: .45; cursor: not-allowed; }

    .fp-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:640px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
    .fp-panel--open  { transform:translateX(0); }
    .fp-panel--close { transform:translateX(100%); }

    .fp-panel-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744); background-size:200% 100%; animation:fpGlow 3s linear infinite; }
    @keyframes fpGlow { 0%{background-position:0%} 100%{background-position:200%} }

    .fp-panel-header { padding:15px 26px; background:linear-gradient(135deg,#0f2744 0%,#1a3d6b 60%,#1d4ed8 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
    .fp-panel-brand { display:flex; align-items:center; gap:13px; }
    .fp-panel-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .fp-panel-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
    .fp-panel-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
    .fp-panel-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
    .fp-panel-close:hover { color:#fff; }

    .fp-panel-body { flex:1; min-height: 0; overflow-y:auto; background:#f8fafc; padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
    .fp-panel-body::-webkit-scrollbar { width:4px; }
    .fp-panel-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

    /* Empty */
    .fp-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; gap:10px; }
    .fp-empty-icon { width:48px; height:48px; border-radius:12px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
    .fp-empty-title { font-size:13.5px; font-weight:700; color:#64748b; }
    .fp-empty-desc { font-size:12px; color:#94a3b8; font-weight:500; text-align:center; }

    /* Footer */
    .fp-panel-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
    .fp-footer-note { display:flex; align-items:center; gap:6px; font-size:12px; color:#94a3b8; font-weight:500; }
    .fp-btn-close { font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:600; color:#64748b; padding:10px 24px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
    .fp-btn-close:hover { background:#f1f5f9; }

    @media (max-width:520px) { .fp-panel { max-width:100%; } .fp-panel-header { padding:16px 18px; } .fp-panel-body { padding:16px; } }
    .dcm-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.35);
        border-top: 2px solid #ffffff;
        border-radius: 50%;
        animation: dcmSpin .7s linear infinite;
    }

    .fp-history-arrow:not(.fp-history-card--open .fp-history-arrow) .dcm-spinner {
        border: 2px solid rgba(37, 99, 235, 0.25);
        border-top: 2px solid #2563eb;
    }

    @keyframes dcmSpin { to { transform: rotate(360deg); } }
        
    /* ==========================================================================
    Schedule Details
    ========================================================================== */

    .fp-card-compact {
        padding: 16px 18px;
    }

    .fp-schedule-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px 24px;
    }

    .fp-schedule-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .fp-schedule-item-full {
        grid-column: 1 / -1;
    }

    /* ==========================================================================
    Status pills
    ========================================================================== */

    .fp-status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .2px;
        white-space: nowrap;
        line-height: 1.6;
    }

    .fp-status-pill::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        flex-shrink: 0;
    }

    .fp-status-pill--lg {
        margin-left: auto;
        padding: 4px 12px;
        font-size: 11.5px;
    }

    .fp-status-green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
    .fp-status-amber { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
    .fp-status-rose  { background: #fef2f2; color: #be123c; border: 1px solid #fecdd3; }
    .fp-status-slate { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }

    /* ==========================================================================
    History Header
    ========================================================================== */
    
    .fp-history-heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 4px;
        margin-bottom: 4px;
    }

    .fp-section-title {
        font-size: 16px;
        font-weight: 800;
        color: #0f172a;
        line-height: 1;
    }

    .fp-section-subtitle {
        margin-top: 4px;
        font-size: 12px;
        color: #64748b;
        font-weight: 500;
    }

    .fp-log-count {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 6px 14px;
        border-radius: 999px;
        background: #eff6ff;
        color: #2563eb;
        border: 1px solid #bfdbfe;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
    }

    .fp-log-count-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #2563eb;
        box-shadow: 0 0 0 3px rgba(37,99,235,.15);
    }

    /* ==========================================================================
    History Card (Accordion)
    ========================================================================== */
    .fp-payment-list{
        display:flex;
        flex-direction:column;
        gap:12px;
    }

    .fp-payment-item{
        border:1px solid #e2e8f0;
        border-radius:12px;
        padding:14px 16px;
        background:#f8fafc;
        transition:.2s;
    }

    .fp-payment-item:hover{
        border-color:#bfdbfe;
        background:#fff;
    }

    .fp-payment-name{
        font-size:14px;
        font-weight:700;
        color:#0f172a;
    }

    .fp-payment-date{
        margin-top:8px;
        display:flex;
        align-items:center;
        gap:10px;
        color:#64748b;
        font-size:13px;
        font-weight:500;
    }

    .fp-payment-arrow{
        color:#2563eb;
        font-size:16px;
        font-weight:700;
    }
    .fp-history-card {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-left: 3px solid transparent;
        border-radius: 16px;
        transition: all .2s ease;
    }

    .fp-history-card:hover {
        border-color: #bfdbfe;
        box-shadow: 0 10px 24px rgba(15,23,42,.08);
    }

    .fp-history-card--open {
        border-left-color: #2563eb;
        box-shadow: 0 10px 24px rgba(15,23,42,.08);
    }

    .fp-history-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 15px 18px;
        cursor: pointer;
        border-radius: 16px;
        transition: background .2s;
    }

    .fp-history-header:hover {
        background: #fafbfc;
    }

    .fp-history-left {
        display: flex;
        align-items: center;
        gap: 13px;
        flex: 1;
        min-width: 0;
    }

    .fp-history-avatar {
        width: 38px;
        height: 38px;
        border-radius: 11px;
        background: linear-gradient(135deg,#0f2744,#2563eb);
        color: #fff;
        font-size: 15px;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        text-transform: uppercase;
    }

    .fp-history-arrow {
        width: 30px;
        height: 30px;
        border-radius: 9px;
        background: #eff6ff;
        color: #2563eb;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: .2s;
    }

    .fp-history-card--open .fp-history-arrow {
        background: #2563eb;
        color: #fff;
    }

    .fp-history-info {
        flex: 1;
        min-width: 0;
    }

    .fp-org-name {
        font-size: 14.5px;
        font-weight: 700;
        color: #0f172a;
        line-height: 1.4;
    }

    .fp-history-meta {
        margin-top: 6px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        row-gap: 6px;
    }

    .fp-history-date {
        font-size: 12px;
        color: #64748b;
        font-weight: 500;
    }

    .fp-meta-dot {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: #cbd5e1;
        flex-shrink: 0;
    }

    .fp-history-body {
        padding: 0 18px 18px;
        animation: fpAccordion .2s ease;
    }

    @keyframes fpAccordion {
        from {
            opacity: 0;
            transform: translateY(-6px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .fp-history-divider {
        height: 1px;
        background: #eef2f7;
        margin: 18px 0;
    }

    /* ========================================================= */

    .fp-section-block {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .fp-history-label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12.5px;
        font-weight: 700;
        color: #334155;
        text-transform: uppercase;
        letter-spacing: .4px;
    }

    .fp-label-icon {
        width: 22px;
        height: 22px;
        border-radius: 7px;
        background: #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        flex-shrink: 0;
    }

    /* =========================================================
    Clients + Payment Methods
    ========================================================= */

    .fp-chip-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .fp-chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 7px 14px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 700;
    }

    .fp-chip-client {
        background: #eff6ff;
        color: #2563eb;
        border: 1px solid #bfdbfe;
    }

    /* ========================================================= */

    @media (max-width:768px){

        .fp-history-header{
            padding:16px;
        }

        .fp-history-body{
            padding:0 16px 16px;
        }

        .fp-chip{
            font-size:11px;
            padding:6px 12px;
        }

        .fp-history-avatar{
            width:32px;
            height:32px;
            font-size:13px;
        }

        .fp-status-pill{
            font-size:10.5px;
        }

    }

    /* ==========================================================================
    Loading
    ========================================================================== */

    .fp-loading {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .fp-loading-card {
        height: 150px;
        border-radius: 16px;
        background: linear-gradient(
            90deg,
            #f1f5f9 25%,
            #ffffff 50%,
            #f1f5f9 75%
        );
        background-size: 200% 100%;
        animation: fpLoading 1.3s linear infinite;
    }

    @keyframes fpLoading {
        from {
            background-position: 200% 0;
        }

        to {
            background-position: -200% 0;
        }
    }

    /* ==========================================================================
    Empty State
    ========================================================================== */

    .fp-empty {
        background: #fff;
        border: 1px dashed #cbd5e1;
        border-radius: 16px;
        padding: 40px 24px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .fp-empty-icon {
        width: 56px;
        height: 56px;
        border-radius: 14px;
        background: #f8fafc;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: #94a3b8;
    }

    .fp-empty-title {
        margin-top: 16px;
        font-size: 15px;
        font-weight: 700;
        color: #334155;
    }

    .fp-empty-desc {
        margin-top: 6px;
        text-align: center;
        max-width: 320px;
        color: #64748b;
        font-size: 13px;
        line-height: 1.6;
    }

    /* ==========================================================================
    Responsive
    ========================================================================== */

    @media (max-width: 768px) {

        .fp-schedule-grid {
            grid-template-columns: 1fr;
            gap: 14px;
        }

        .fp-schedule-item-full {
            grid-column: auto;
        }

        .fp-history-heading {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }

        .fp-history-header {
            gap: 12px;
        }

        .fp-chip {
            font-size: 11px;
            padding: 6px 12px;
        }
    }
    `}</style>
);